import { Product } from '../data/products'

export type SortKey = 'price-desc' | 'popularity'

export const YARBO_POPULARITY_RANK_BY_SLUG: Record<string, number> = {
  'yarbo-lawn-mower-pro': 1,
  'yarbo-lawn-mower-snow-blower-blower': 2,
  'yarbo-lawn-mower-snow-blower': 3,
  'yarbo-snow-blower-blower': 4,
  'yarbo-lawn-mower-pro-snow-blower-blower': 5,
  'yarbo-snow-blower': 6,
  'yarbo-lawn-mower-blower': 7,
  'yarbo-lawn-mower-pro-snow-blower': 8,
  'yarbo-lawn-mower': 9,
  'yarbo-lawn-mower-pro-blower': 10,
  'yarbo-blower': 11,
  'yarbo-core': 12,
  'snow-blower-module': 13,
  'lawn-mower-module': 14,
  'lawn-mower-pro-module': 15,
  'blower-module': 16,
  'trimmer-module': 17,
  'smart-assist-module': 18,
  'snow-plow-blade': 19,
  'cutting-disc-and-bolts': 20,
  'cutting-blades-and-bolts-40-pcs': 21,
  'straight-blades-and-bolts': 22,
  'rtk-antenna-for-data-center-and-rover': 23,
  'battery-power-cord': 24,
  'yarbo-track-grease': 25,
  'tow-hitch': 26,
  'snow-blower-module-cover': 27,
  '38-4ah-battery': 28,
  'docking-station': 29,
  'data-center': 30,
  'remote-controller': 31,
  'wired-charger': 32,
  'snow-track': 33,
  'lawn-track': 34,
  'scraper-bar': 35,
  'weighted-side-plate': 36,
  'back-brace-mount': 37,
  'rtk-antenna-pro-for-data-center-and-rover': 38,
  'high-rtk-antenna-mount': 39,
  'halow-antenna-for-rover': 40,
  'halow-antenna-for-data-center': 41,
  'bluetooth-antenna-for-data-center': 42,
  'poe-power-adapter': 43,
  'core-cover': 44,
  'lawn-mower-module-cover': 45,
  'blower-module-cover': 46,
  'smart-assist-module-cover': 47,
  'shear-pins-and-cotter-pins-set': 48,
  'anti-slip-studs-for-rubber-track': 49,
  'snow-shovel': 50,
  'trimmer-line-spool': 51,
  'cutting-disc-and-bolts-for-lawn-mower-pro-module': 52,
  'mini-yarbo-lawn-mower-3d-model': 53,
  'mini-yarbo-snow-blower-3d-model': 54,
  'mini-yarbo-core-3d-model': 55,
  'mini-yarbo-blower-3d-model': 56,
  'yarbo-down-jacket': 57,
  'yarbo-t-shirt': 58,
  'yarbo-cap': 59,
}

const FALLBACK_GROUP_RANK: Record<string, number> = {
  'Complete System': 0,
  Bundle: 1,
  Module: 2,
  Accessory: 3,
  Apparel: 4,
  Other: 5,
}

const APPAREL_SLUGS = new Set([
  'yarbo-down-jacket',
  'yarbo-t-shirt',
  'yarbo-cap',
  'mini-yarbo-lawn-mower-3d-model',
  'mini-yarbo-snow-blower-3d-model',
  'mini-yarbo-core-3d-model',
  'mini-yarbo-blower-3d-model',
])

/**
 * Robust price-to-number parser. Accepts "$1,199", "1199", 1199, null/undefined.
 * Returns Number.NaN when unparseable, so missing prices can be pushed to the bottom.
 */
export function parseProductPrice(price: unknown): number {
  if (typeof price === 'number') return isFinite(price) ? price : NaN
  if (typeof price === 'string') {
    const cleaned = price.replace(/[^0-9.\-]/g, '')
    if (cleaned === '') return NaN
    const n = parseFloat(cleaned)
    return isFinite(n) ? n : NaN
  }
  return NaN
}

function fallbackGroupRank(p: Product): number {
  if (p.slug && APPAREL_SLUGS.has(p.slug)) return FALLBACK_GROUP_RANK.Apparel
  const g = p.primaryGroup as string | undefined
  if (g && g in FALLBACK_GROUP_RANK) return FALLBACK_GROUP_RANK[g]
  return FALLBACK_GROUP_RANK.Other
}

/**
 * Returns the popularity rank for a product.
 * Lower number = more popular. Products not in the map get a large fallback
 * computed from their group + (negative) price so higher-priced items in the
 * same fallback bucket beat cheaper ones.
 */
export function getPopularityRank(p: Product): number {
  if (typeof (p as any).popularityRank === 'number') {
    return (p as any).popularityRank
  }
  if (p.slug && YARBO_POPULARITY_RANK_BY_SLUG[p.slug] != null) {
    return YARBO_POPULARITY_RANK_BY_SLUG[p.slug]
  }
  // Fallback: group bucket (×10000) + inverse price so the more expensive
  // product in the same bucket ranks ahead of the cheaper one.
  const groupBucket = fallbackGroupRank(p) * 10000
  const price = parseProductPrice(p.price)
  const priceOffset = isFinite(price)
    ? Math.max(0, 9999 - Math.floor(price))
    : 9999
  return 100000 + groupBucket + priceOffset
}

/**
 * Sort a product list for storefront display.
 *  - 'price-desc' (default): price high → low, popularity asc, name asc.
 *  - 'popularity': popularityRank asc, fallback group, price desc, name asc.
 * Products with missing/invalid prices are always pushed to the bottom.
 */
export function sortProducts<T extends Product>(
  list: T[],
  sortKey: SortKey = 'price-desc',
): T[] {
  const arr = [...list]

  arr.sort((a, b) => {
    const priceA = parseProductPrice(a.price)
    const priceB = parseProductPrice(b.price)
    const aMissing = !isFinite(priceA)
    const bMissing = !isFinite(priceB)

    // Always push missing-price products to the bottom.
    if (aMissing && !bMissing) return 1
    if (!aMissing && bMissing) return -1

    if (sortKey === 'popularity') {
      const popA = getPopularityRank(a)
      const popB = getPopularityRank(b)
      if (popA !== popB) return popA - popB
      // Tie: price desc, then name asc
      if (!aMissing && !bMissing && priceA !== priceB) return priceB - priceA
      return (a.name || '').localeCompare(b.name || '')
    }

    // Default: price desc
    if (!aMissing && !bMissing && priceA !== priceB) return priceB - priceA
    const popA = getPopularityRank(a)
    const popB = getPopularityRank(b)
    if (popA !== popB) return popA - popB
    return (a.name || '').localeCompare(b.name || '')
  })

  return arr
}

export function isValidSortKey(
  value: string | null | undefined,
): value is SortKey {
  return value === 'price-desc' || value === 'popularity'
}

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'popularity', label: 'Popularity' },
]
